/** Usernames may include extra @, e.g. Roshan@123. Trailing punctuation is not consumed. */
const MENTION_USERNAME_REGEX = /@([A-Za-z0-9._-]+(?:@[A-Za-z0-9._-]+)*)/g;
const MENTION_CHANNEL_REGEX = /#([A-Za-z0-9][A-Za-z0-9._-]*)/g;
const MENTION_TOKEN_REGEX = /(@[A-Za-z0-9._-]+(?:@[A-Za-z0-9._-]+)*|#[A-Za-z0-9][A-Za-z0-9._-]*)/g;

export const MENTION_TAG_CLASS = 'workloop-mention-tag';
export const ALL_MENTION_USERNAME = 'all';

export const isEveryoneMention = (value) =>
  String(value || '').toLowerCase() === ALL_MENTION_USERNAME;

export const extractMentionUsernames = (plainText) => {
  if (!plainText) return [];
  const names = [];
  const regex = new RegExp(MENTION_USERNAME_REGEX.source, 'g');
  let match;
  while ((match = regex.exec(plainText)) !== null) {
    if (match[1]) names.push(match[1]);
  }
  return names;
};

/** Start of the current @user or #channel token (leftmost trigger, so user@id stays one mention). */
export const findMentionTriggerIndex = (text, cursorIndex) => {
  if (!text || cursorIndex <= 0) {
    return { index: -1, type: null };
  }
  let tokenStart = cursorIndex;
  for (let i = cursorIndex - 1; i >= 0; i -= 1) {
    if (text[i] === ' ' || text[i] === '\n' || text[i] === '\t') {
      break;
    }
    tokenStart = i;
  }
  if (text[tokenStart] === '@') {
    return { index: tokenStart, type: 'user' };
  }
  if (text[tokenStart] === '#') {
    return { index: tokenStart, type: 'channel' };
  }
  return { index: -1, type: null };
};

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const escapeAttr = (value) => escapeHtml(value);

export const formatUserDisplayName = (user) => {
  if (!user) return '';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return fullName || user.username || '';
};

export const getUserMentionDisplayToken = (users, username) => {
  if (!username) return '@';
  const user = findUserByMention(users, username);
  const displayName = formatUserDisplayName(user);
  return displayName ? `@${displayName}` : `@${username}`;
};

const wrapMentionToken = (token, { users = [], channels = [] } = {}) => {
  if (token.startsWith('@')) {
    const username = token.slice(1);
    if (isEveryoneMention(username)) {
      return `<span class="${MENTION_TAG_CLASS}" data-mention-type="user" data-mention-value="${escapeAttr(ALL_MENTION_USERNAME)}" role="link" tabindex="0">@all</span>`;
    }
    const user = findUserByMention(users, username);
    if (!user) {
      return escapeHtml(token);
    }
    const displayToken = getUserMentionDisplayToken(users, username);
    return `<span class="${MENTION_TAG_CLASS}" data-mention-type="user" data-mention-value="${escapeAttr(username)}" role="link" tabindex="0">${escapeHtml(displayToken)}</span>`;
  }
  if (token.startsWith('#')) {
    const channelName = token.slice(1);
    const channel = findChannelByMention(channels, channelName);
    if (!channel) {
      return escapeHtml(token);
    }
    const displayToken = `#${channel.name}`;
    return `<span class="${MENTION_TAG_CLASS}" data-mention-type="channel" data-mention-value="${escapeAttr(channelName)}" role="link" tabindex="0">${escapeHtml(displayToken)}</span>`;
  }
  return escapeHtml(token);
};

export const wrapPlainMentionText = (text, mentionContext = {}) => {
  if (!text) return '';
  return text.replace(MENTION_TOKEN_REGEX, (match) => wrapMentionToken(match, mentionContext));
};

export const enrichMentionHtml = (html, mentionContext = {}) => {
  if (!html) return '';
  const parts = html.split(/(<[^>]+>)/g);
  return parts.map((part) => {
    if (!part || part.startsWith('<')) {
      return part;
    }
    return wrapPlainMentionText(part, mentionContext);
  }).join('');
};

export const getMentionSegmentDisplayText = (segment, users = [], channels = []) => {
  if (!segment || segment.type === 'text') {
    return segment?.value || '';
  }
  if (segment.type === 'user') {
    if (isEveryoneMention(segment.value)) return '@all';
    return getUserMentionDisplayToken(users, segment.value);
  }
  const channel = findChannelByMention(channels, segment.value);
  return channel?.name ? `#${channel.name}` : segment.raw;
};

export const parsePlainTextMentionSegments = (text, mentionContext = {}) => {
  if (!text) return [];

  const { users = [], channels = [] } = mentionContext;
  const segments = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(MENTION_TOKEN_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.substring(lastIndex, match.index) });
    }

    const token = match[0];
    if (token.startsWith('@')) {
      const username = token.slice(1);
      if (isEveryoneMention(username) || findUserByMention(users, username)) {
        segments.push({ type: 'user', value: username, raw: token });
      } else {
        segments.push({ type: 'text', value: token });
      }
    } else {
      const channelName = token.slice(1);
      if (findChannelByMention(channels, channelName)) {
        segments.push({ type: 'channel', value: channelName, raw: token });
      } else {
        segments.push({ type: 'text', value: token });
      }
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.substring(lastIndex) });
  }

  return segments.length ? segments : [{ type: 'text', value: text }];
};

export const findUserByMention = (users, mentionValue) => {
  if (!mentionValue || !Array.isArray(users)) return null;
  const needle = mentionValue.toLowerCase();
  return users.find((user) => user.username?.toLowerCase() === needle) || null;
};

export const findChannelByMention = (channels, mentionValue) => {
  if (!mentionValue || !Array.isArray(channels)) return null;
  const needle = mentionValue.toLowerCase();
  return channels.find((channel) => {
    if (channel.type === 'direct' || channel.type === 'DIRECT_MESSAGE') {
      return false;
    }
    const name = channel.name?.toLowerCase() || '';
    const slug = name.replace(/\s+/g, '-');
    return name === needle || slug === needle;
  }) || null;
};

export { MENTION_USERNAME_REGEX, MENTION_CHANNEL_REGEX };
